import BookingTestCard from '@sharedComponents/BookingTestCard';
import Button from '@sharedComponents/Button';
import FlexBox from '@sharedComponents/FlexBox';
import Grid from '@sharedComponents/Grid';
import Loader from '@sharedComponents/Loader';
import Spacer from '@sharedComponents/Spacer';
import Typography from '@sharedComponents/Typography';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useClearRefinements, useHits } from 'react-instantsearch';

/**
 * Builds the card shape from an Algolia hit, whose fields vary with the indexed
 * record: some carry `tests` levels, some only a reference.
 */
const toCardShape = (item) => ({
    id: item.referenceId || item.id,
    name: item.title || item.name || [{ value: item.collection, language: 'fr' }],
    tests: item.tests || [{ id: item.referenceId || item.id, name: item.title || item.name || item.referenceId, isSelected: false }],
    tags: item.tags || [],
    _algoliaScore: item._rankingInfo?.score,
    ...item,
});

/**
 * Result grid of an InstantSearch context. Must be rendered inside
 * `<InstantSearch>`.
 *
 * In `readOnly` the cards lose their selection button and the group tag: the
 * catalogue browses the hits, it never selects one. The selection props are then
 * unused, which is why they are all optional.
 */
const AlgoliaTestHits = ({
    tests = [],
    actions,
    client,
    isTestSelected,
    isTestInSelectedGroup,
    getClientSpecificTag,
    openTestDetails,
    gridRef,
    readOnly = false,
}) => {
    const { t } = useTranslation();
    const { items, results } = useHits();
    // Default excludedAttributes is ['query']: without emptying it the button would
    // clear the facets but leave the text search active.
    const { refine: clearRefinements, canRefine: canClearRefinements } = useClearRefinements({ excludedAttributes: [] });

    if (!results) {
        return <Loader />;
    }

    if (items.length === 0) {
        return (
            <FlexBox gap="xs" justifyContent="center" alignItems="center" flexDirection="column">
                <Spacer y="m" />
                <Typography fontWeight="medium">{t("We haven't found any tests matching your search.")}</Typography>
                <Typography>{t('Please check or modify your search.')}</Typography>
                {canClearRefinements && (
                    <>
                        <Spacer y="s" />
                        <Button label={t('Reset filters')} action={clearRefinements} ghost />
                    </>
                )}
            </FlexBox>
        );
    }

    return (
        <>
            <FlexBox justifyContent="space-between" alignItems="center">
                <Typography fontSize="sm" color="neutral.600">
                    {results.nbHits} {t('results found')}
                </Typography>
            </FlexBox>
            <Spacer y="s" />
            <div ref={gridRef}>
                <Grid>
                    {items.map((item, i) => {
                        const test = toCardShape(item);
                        const key = item.referenceId || item.id || i;

                        if (readOnly) {
                            // `groupTest` suppresses the add button: without a selection
                            // context `action` would be undefined.
                            return <BookingTestCard key={key} test={test} onView={() => openTestDetails(test)} groupTest />;
                        }

                        const _tests = tests.find((t) => t.id === test.id) || test;
                        const selectedTests = _tests.tests?.filter((t) => t.isSelected) || [];

                        return (
                            <BookingTestCard
                                key={key}
                                test={test}
                                selectedTests={selectedTests}
                                isSelected={isTestSelected(test)}
                                partiallySelected={_tests.tests?.some((level) => level.isSelected)}
                                action={(actionType = null) => {
                                    if (_tests.tests.length === selectedTests.length || actionType === 'remove') {
                                        actions.unSelect({ ...test, group: 'TESTS' });
                                    } else {
                                        const hasMultipleLevels = test.tests.length > 1;
                                        if (hasMultipleLevels) {
                                            openTestDetails(test);
                                        } else {
                                            actions.select({
                                                ...test,
                                                group: 'TESTS',
                                                tests: [{ ...test.tests[0], isSelected: true }],
                                            });
                                        }
                                    }
                                }}
                                onView={() => openTestDetails(test)}
                                tag={isTestInSelectedGroup(test) ?? getClientSpecificTag(test, client)}
                            />
                        );
                    })}
                </Grid>
            </div>
        </>
    );
};

AlgoliaTestHits.propTypes = {
    /** Current booking selection. Unused in read-only. */
    tests: PropTypes.array,
    actions: PropTypes.object,
    client: PropTypes.object,
    isTestSelected: PropTypes.func,
    isTestInSelectedGroup: PropTypes.func,
    getClientSpecificTag: PropTypes.func,
    openTestDetails: PropTypes.func.isRequired,
    gridRef: PropTypes.object,
    readOnly: PropTypes.bool,
};

export default AlgoliaTestHits;
